import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project } from '../../types';
import { dossierAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BRAND, faseKleur } from '../../styles/brand';
import { FaseTag } from '../flow/FaseTag';
import { htmlToPlain } from '../common/RichText';
import { LockBar } from '../dossier/LockBar';
import { useSectionClear } from '../dossier/useSectionClear';

interface WerkplaatsModuleProps {
  project: Project;
}

interface MiddelType { key: string; label: string; omschrijving: string; vorm: string }

interface Middel {
  id: string;
  type: string;
  typeLabel: string;
  personaIds: string[];
  personaNamen: string[];
  kanaal: string;
  instructie: string;
  titel: string;
  tekst: string;
  toelichting: string;
  openpunten: string[];
  aangemaaktOp: string;
}

// Terugval als de lijst met middeltypen niet opgehaald kan worden (blijft in stap met de backend).
const FALLBACK_TYPES: MiddelType[] = [
  { key: 'bewonersbrief', label: 'Bewonersbrief', omschrijving: 'Brief aan bewoners en omwonenden.', vorm: '' },
  { key: 'webtekst', label: 'Webtekst', omschrijving: 'Pagina op de projectwebsite.', vorm: '' },
  { key: 'persbericht', label: 'Persbericht', omschrijving: 'Bericht voor regionale en vakmedia.', vorm: '' },
  { key: 'socialpost', label: 'Socialmediabericht', omschrijving: 'Bericht voor de kanalen van de doelgroep.', vorm: '' },
  { key: 'woordvoeringslijn', label: 'Woordvoeringslijn', omschrijving: 'Interne lijn voor woordvoering en Q&A.', vorm: '' },
  { key: 'ontwerpbriefing', label: 'Briefing voor een ontwerper', omschrijving: 'Opdracht voor een ontwerper.', vorm: '' },
];

const card: React.CSSProperties = { border: '1px solid #ececf1', borderRadius: 12, padding: 20, background: '#fff' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13.5, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 };
const hint: React.CSSProperties = { fontSize: 12.5, color: '#8a90a0', marginBottom: 8 };
function ctrl(disabled: boolean): React.CSSProperties {
  return { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, fontFamily: 'inherit', color: '#20242e', background: disabled ? '#f6f7f9' : '#fff', boxSizing: 'border-box' };
}
const chip = (bg: string, color: string): React.CSSProperties => ({ fontSize: 11.5, fontWeight: 600, background: bg, color, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' });

let seq = 0;
function newId(): string {
  seq += 1;
  try { if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID(); } catch (e) { /* fallback */ }
  return `m-${seq}-${Math.round(Math.random() * 1e6)}`;
}

/** Namen van de persona's waarvoor het middel is gemaakt (oudere middelen hadden er één). */
const middelPersonas = (m: any): string[] => (Array.isArray(m?.personaNamen) ? m.personaNamen.filter(Boolean) : (m?.personaNaam ? [m.personaNaam] : []));

const datumNL = (iso: string) => {
  try { return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { return ''; }
};

/**
 * Communicatiewerkplaats (na het Strategisch Fundament): zet per persona een middel op vanuit de
 * gevalideerde kernboodschap. De AI levert een opzet; de adviseur schaaft bij. Opslag in de
 * dossiersectie 'communicatiewerkplaats'.
 */
export const WerkplaatsModule: React.FC<WerkplaatsModuleProps> = ({ project }) => {
  const toast = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState<MiddelType[]>(FALLBACK_TYPES);
  const [middelen, setMiddelen] = useState<Middel[]>([]);
  const [personas, setPersonas] = useState<any[]>([]);
  const [kernboodschap, setKernboodschap] = useState('');
  const [kernboodschapGevalideerd, setKernboodschapGevalideerd] = useState(false);
  const [heeftStrategie, setHeeftStrategie] = useState(false);
  const [type, setType] = useState('bewonersbrief');
  const [personaIds, setPersonaIds] = useState<string[]>([]);
  const [kanaal, setKanaal] = useState('');
  const [instructie, setInstructie] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Valideren & vastleggen, net als fase 1-5; daarmee is het project in het overzicht voltooid.
  const [status, setStatus] = useState<'open' | 'locked'>('open');
  const [lockedAt, setLockedAt] = useState<string | null>(null);
  const [locking, setLocking] = useState(false);
  const locked = status === 'locked';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      const secties = res?.dossier?.sections || {};
      const fase5 = secties.kernboodschap || {};
      setKernboodschap(htmlToPlain(fase5.content?.kernboodschap || ''));
      setKernboodschapGevalideerd(fase5.status === 'locked');
      const ps = secties.personas?.content?.personas;
      setPersonas(Array.isArray(ps) ? ps : []);
      setHeeftStrategie(Array.isArray(secties.strategie?.content?.doelgroepen) && secties.strategie.content.doelgroepen.length > 0);
      const lijst = secties.communicatiewerkplaats?.content?.middelen;
      setMiddelen(Array.isArray(lijst) ? lijst : []);
      setStatus(secties.communicatiewerkplaats?.status === 'locked' ? 'locked' : 'open');
      setLockedAt(secties.communicatiewerkplaats?.lockedAt || null);
      setDirty(false);
    } catch (e) {
      // dossier wordt server-side aangemaakt zodra beschikbaar
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await dossierAPI.middelTypes();
        if (!cancelled && Array.isArray(res?.types) && res.types.length) setTypes(res.types);
      } catch (e) { /* terugval op FALLBACK_TYPES */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Standaard staan alle persona's aan; de adviseur kan er groepen uit halen.
  const [personaKeuzeGezet, setPersonaKeuzeGezet] = useState(false);
  useEffect(() => {
    if (!personaKeuzeGezet && personas.length) {
      setPersonaIds(personas.map((p) => p.id));
      setPersonaKeuzeGezet(true);
    }
  }, [personas, personaKeuzeGezet]);

  const togglePersona = (id: string) => setPersonaIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const persist = async (lijst: Middel[]) => {
    await dossierAPI.saveSection(project.id, 'communicatiewerkplaats', { middelen: lijst });
  };

  const genereer = async () => {
    if (personaIds.length === 0) { toast.error('Geen persona gekozen', "Kies minimaal één persona uit Fase 3."); return; }
    setBusy(true);
    try {
      const res = await dossierAPI.assistMiddel(project.id, { type, personaIds, kanaal: kanaal.trim(), instructie: instructie.trim() });
      const m = res?.middel || {};
      const gekozen = personas.filter((p) => personaIds.includes(p.id));
      const nieuw: Middel = {
        id: newId(),
        type,
        typeLabel: types.find((t) => t.key === type)?.label || type,
        personaIds: [...personaIds],
        personaNamen: gekozen.map((p) => p.naam || 'Naamloze persona'),
        kanaal: kanaal.trim(),
        instructie: instructie.trim(),
        titel: m.titel || '',
        tekst: m.tekst || '',
        toelichting: m.toelichting || '',
        openpunten: Array.isArray(m.openpunten) ? m.openpunten : [],
        aangemaaktOp: new Date().toISOString(),
      };
      const lijst = [nieuw, ...middelen];
      setMiddelen(lijst);
      try { await persist(lijst); setDirty(false); } catch (e) { setDirty(true); }
      toast.success('Opzet klaar', 'Beoordeel de tekst en schaaf bij; dit is een concept.');
    } catch (e: any) {
      toast.error('Genereren mislukt', e?.response?.data?.error || 'Probeer het later opnieuw.');
    } finally { setBusy(false); }
  };

  const updateMiddel = (id: string, key: keyof Middel, value: string) => {
    setMiddelen((l) => l.map((m) => (m.id === id ? { ...m, [key]: value } : m)));
    setDirty(true);
  };

  const opslaan = async () => {
    setSaving(true);
    try {
      await persist(middelen);
      setDirty(false);
      toast.success('Opgeslagen', 'De middelen staan in het projectdossier.');
    } catch (e: any) {
      toast.error('Opslaan mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setSaving(false); }
  };

  const verwijder = async (id: string) => {
    const m = middelen.find((x) => x.id === id);
    if (m && !window.confirm(`"${m.titel || m.typeLabel}" verwijderen?`)) return;
    const lijst = middelen.filter((x) => x.id !== id);
    setMiddelen(lijst);
    try { await persist(lijst); setDirty(false); } catch (e) { setDirty(true); }
  };

  const doLock = async () => {
    setLocking(true);
    try {
      // Ook nog niet opgeslagen bewerkingen gaan mee in de vastgelegde versie.
      const res = await dossierAPI.lockSection(project.id, 'communicatiewerkplaats', { middelen });
      setStatus('locked');
      setLockedAt(res?.section?.lockedAt || new Date().toISOString());
      setDirty(false);
      toast.success('Fase gevalideerd', 'De middelen zijn vastgelegd in het projectdossier.');
    } catch (e: any) {
      toast.error('Valideren mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const doUnlock = async () => {
    setLocking(true);
    try {
      await dossierAPI.unlockSection(project.id, 'communicatiewerkplaats');
      setStatus('open');
      toast.success('Fase heropend', 'Je kunt middelen weer aanpassen, toevoegen en opnieuw valideren.');
    } catch (e: any) {
      toast.error('Heropenen mislukt', e?.response?.data?.error || 'Probeer het opnieuw.');
    } finally { setLocking(false); }
  };

  const { canClear, clearing, doClear } = useSectionClear(project.id, project.name, 'communicatiewerkplaats', 'Communicatiewerkplaats', { middelen: [] }, load);

  const kopieer = async (m: Middel) => {
    try {
      await navigator.clipboard.writeText(`${m.titel}\n\n${m.tekst}`);
      toast.success('Gekopieerd', 'De tekst staat op je klembord.');
    } catch (e) {
      toast.error('Kopiëren mislukt', 'Selecteer de tekst en kopieer handmatig.');
    }
  };

  if (loading) return <div style={{ padding: 32, color: '#6b7280' }}>Projectdossier laden…</div>;

  const fk = faseKleur(6);
  const actiefType = types.find((t) => t.key === type);
  const klaar = kernboodschapGevalideerd && personas.length > 0 && !locked;
  const taalniveaus = Array.from(new Set(personas.filter((p) => personaIds.includes(p.id)).map((p) => String(p.taalniveau || '').trim()).filter(Boolean)));

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1000, margin: '0 auto' }}>
      <FaseTag nr={6} />
      <h1 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 700, color: BRAND.groen, letterSpacing: '-0.3px' }}>De communicatiewerkplaats</h1>
      <p style={{ margin: '0 0 20px', color: '#555', fontSize: 15, lineHeight: 1.6 }}>
        Zet hier middelen op vanuit de <strong>gevalideerde kernboodschap</strong>, gericht op de persona's die je kiest.
        De AI levert een opzet op basis van het fundament; jij schaaft bij. Er komt bewust geen opgemaakt
        eindproduct uit — de tekst is het startpunt voor de schrijver of ontwerper.
      </p>

      {!kernboodschapGevalideerd && (
        <div style={{ ...card, background: fk.soft, borderColor: fk.border, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: BRAND.groen, marginBottom: 4 }}>Eerst de kernboodschap valideren</div>
          <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.6 }}>
            De werkplaats bouwt op een gedragen boodschap. Valideer Fase 5 voordat je middelen laat opzetten.
          </div>
          <button onClick={() => navigate(`/project/${project.id}/kernboodschap`)}
            style={{ marginTop: 12, padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: BRAND.groen, color: '#fff', cursor: 'pointer' }}>
            Naar de kernboodschap
          </button>
        </div>
      )}

      {kernboodschapGevalideerd && personas.length === 0 && (
        <div style={{ ...card, background: fk.soft, borderColor: fk.border, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: BRAND.groen, marginBottom: 4 }}>Nog geen persona's</div>
          <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.6 }}>Een middel is gericht op één of meer persona's. Maak die eerst in Fase 3.</div>
          <button onClick={() => navigate(`/project/${project.id}/personas-fase`)}
            style={{ marginTop: 12, padding: '9px 16px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, border: 'none', background: BRAND.groen, color: '#fff', cursor: 'pointer' }}>
            Naar de persona's
          </button>
        </div>
      )}

      {/* Generator */}
      <div style={{ ...card, background: BRAND.papier, borderColor: BRAND.rand, marginBottom: 24, opacity: klaar ? 1 : 0.6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle} htmlFor="werkplaats-type">Welk middel</label>
            <select id="werkplaats-type" value={type} disabled={!klaar} onChange={(e) => setType(e.target.value)} style={ctrl(!klaar)}>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            {actiefType?.omschrijving && <div style={{ ...hint, marginTop: 6, marginBottom: 0 }}>{actiefType.omschrijving} {actiefType.vorm}</div>}
          </div>
          <div>
            <label style={labelStyle}>Voor welke persona's</label>
            <div style={{ ...hint, marginBottom: 0 }}>
              {personaIds.length === 0
                ? 'Kies er minimaal één.'
                : personaIds.length === personas.length
                  ? `Alle ${personas.length} persona's; de tekst moet voor iedereen werken.`
                  : `${personaIds.length} van ${personas.length} persona's.`}
              {taalniveaus.length > 0 && ` Taalniveau: ${taalniveaus.join(', ')}.`}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {personas.map((p) => {
              const actief = personaIds.includes(p.id);
              return (
                <button key={p.id} type="button" disabled={!klaar} onClick={() => togglePersona(p.id)} aria-pressed={actief}
                  title={p.groep || undefined}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, border: `1px solid ${actief ? BRAND.groen : BRAND.rand}`, background: actief ? BRAND.groen : '#fff', color: actief ? '#fff' : '#4b5563', cursor: klaar ? 'pointer' : 'default' }}>
                  {actief && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  {p.naam || 'Naamloze persona'}
                </button>
              );
            })}
          </div>
          {personas.length > 1 && (
            <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
              <button type="button" disabled={!klaar} onClick={() => setPersonaIds(personas.map((p) => p.id))}
                style={{ padding: 0, border: 'none', background: 'none', color: BRAND.groen, fontSize: 12.5, fontWeight: 600, cursor: klaar ? 'pointer' : 'default' }}>Alle persona's</button>
              <button type="button" disabled={!klaar} onClick={() => setPersonaIds([])}
                style={{ padding: 0, border: 'none', background: 'none', color: '#8a90a0', fontSize: 12.5, fontWeight: 600, cursor: klaar ? 'pointer' : 'default' }}>Geen</button>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginTop: 14 }}>
          <div>
            <label style={labelStyle} htmlFor="werkplaats-kanaal">Kanaal of drager <span style={{ fontWeight: 400, color: '#8a90a0' }}>(optioneel)</span></label>
            <input id="werkplaats-kanaal" value={kanaal} disabled={!klaar} onChange={(e) => setKanaal(e.target.value)} placeholder="bijv. huis-aan-huisbrief, LinkedIn, projectwebsite" style={ctrl(!klaar)} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="werkplaats-instructie">Extra instructie <span style={{ fontWeight: 400, color: '#8a90a0' }}>(optioneel)</span></label>
            <input id="werkplaats-instructie" value={instructie} disabled={!klaar} onChange={(e) => setInstructie(e.target.value)} placeholder="bijv. aanleiding: start werkzaamheden in mei" style={ctrl(!klaar)} />
          </div>
        </div>

        {/* Waar de opzet op rust */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 14, paddingTop: 14, borderTop: `1px solid ${BRAND.rand}` }}>
          <span style={{ fontSize: 11.5, color: '#9aa1b0', fontWeight: 600 }}>Voeding:</span>
          {[
            { on: kernboodschapGevalideerd, label: 'Kernboodschap', to: 'kernboodschap' },
            { on: personas.length > 0, label: "Persona's", to: 'personas-fase' },
            { on: heeftStrategie, label: 'Strategie per doelgroep', to: 'strategie-fase' },
          ].map((s) => (
            <button key={s.label} onClick={() => navigate(`/project/${project.id}/${s.to}`)}
              title={s.on ? 'Wordt meegenomen' : 'Nog niet ingevuld — klik om aan te vullen'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: s.on ? '#cdebd6' : BRAND.rand, background: s.on ? '#f2fbf5' : '#fff', color: s.on ? '#1f7a44' : BRAND.groenMuted }}>
              <span>{s.on ? '✓' : '+'}</span>{s.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={genereer} disabled={!klaar || busy || personaIds.length === 0}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: BRAND.groen, color: '#fff', cursor: !klaar || busy || personaIds.length === 0 ? 'default' : 'pointer', opacity: !klaar || busy || personaIds.length === 0 ? 0.6 : 1 }}>
            {busy ? 'AI schrijft…' : 'Opzet genereren'}
          </button>
        </div>
      </div>

      {kernboodschap && (
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8a90a0', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>Gevalideerde kernboodschap</div>
          <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{kernboodschap}</div>
        </div>
      )}

      {/* Opgezette middelen */}
      {middelen.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: '#8a90a0', fontSize: 14 }}>
          Nog geen middelen opgezet. Kies hierboven een middel en een persona.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {middelen.map((m) => (
            <div key={m.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <input value={m.titel} readOnly={locked} onChange={(e) => updateMiddel(m.id, 'titel', e.target.value)}
                    style={{ width: '100%', minWidth: 240, border: 'none', padding: 0, fontSize: 17, fontWeight: 800, color: '#1a1a2e', background: 'transparent', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    <span style={chip(fk.soft, BRAND.groen)}>{m.typeLabel}</span>
                    {middelPersonas(m).length > 0 && (
                      <span style={chip('#f1f2f5', '#4b5563')} title={middelPersonas(m).join(', ')}>
                        {middelPersonas(m).length <= 2 ? middelPersonas(m).join(', ') : `${middelPersonas(m).length} persona's`}
                      </span>
                    )}
                    {m.kanaal && <span style={chip('#f1f2f5', '#4b5563')}>{m.kanaal}</span>}
                    <span style={{ fontSize: 11.5, color: '#9aa1b0', alignSelf: 'center' }}>{datumNL(m.aangemaaktOp)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => kopieer(m)} style={{ padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: `1px solid ${BRAND.rand}`, background: '#fff', color: BRAND.groen, cursor: 'pointer' }}>Kopieer tekst</button>
                  {!locked && <button onClick={() => verwijder(m.id)} style={{ padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, border: '1px solid #f0c9c9', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>Verwijderen</button>}
                </div>
              </div>

              <textarea value={m.tekst} readOnly={locked} onChange={(e) => updateMiddel(m.id, 'tekst', e.target.value)}
                style={{ width: '100%', minHeight: 260, resize: 'vertical', padding: '12px 14px', borderRadius: 8, border: '1px solid #d8dce4', fontSize: 14, lineHeight: 1.65, fontFamily: 'inherit', color: '#20242e', background: locked ? '#f6f7f9' : '#fff', boxSizing: 'border-box' }} />

              {m.toelichting && (
                <div style={{ marginTop: 10, fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 }}>
                  <strong style={{ color: '#4b5563' }}>Keuzes van de AI:</strong> {m.toelichting}
                </div>
              )}
              {m.openpunten.length > 0 && (
                <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: '#fef8ec', border: '1px solid #f3e3c3' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Nog invullen of controleren</div>
                  <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 3 }}>
                    {m.openpunten.map((o, i) => <li key={i} style={{ fontSize: 12.5, color: '#7c4a11', lineHeight: 1.5 }}>{o}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {dirty && !locked && (
        // Niet sticky: de validatiebalk eronder plakt al onderaan het scherm.
        <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 10, background: '#fff', border: `1px solid ${BRAND.rand}`, boxShadow: '0 -2px 12px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, color: '#4b5563' }}>Je hebt wijzigingen die nog niet zijn opgeslagen.</span>
          <button onClick={opslaan} disabled={saving}
            style={{ padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, border: 'none', background: BRAND.groen, color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Opslaan…' : 'Wijzigingen opslaan'}
          </button>
        </div>
      )}

      <LockBar
        status={status}
        onLock={doLock}
        onUnlock={doUnlock}
        lockedAt={lockedAt}
        canLock={middelen.length > 0}
        busy={locking}
        faseLabel="Communicatiewerkplaats"
        canClear={canClear}
        onClear={doClear}
        clearing={clearing}
      />
      {middelen.length === 0 && !locked && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: '#8a90a0', textAlign: 'right' }}>
          Zet minimaal één middel op om te kunnen valideren.
        </div>
      )}
    </div>
  );
};
